/**
 * The one place that decides what may leave the browser in an analytics event.
 *
 * Two scrubbers existed: `campaignAnalytics.scrub` for the `/c/` funnel and a
 * second, weaker one inside `landing/landingTrack.js` for `/lp/*`. They were
 * not merely duplicated, they DISAGREED, and both disagreements are guarded by
 * a test, so neither could simply adopt the other:
 *
 *   - `/c/` drops arrays outright. `/lp/` must keep `topics: ['Health']` and
 *     `fields_touched: ['email','topics']`, which are the shape of two of its
 *     nineteen events.
 *   - `/lp/` bans `publication`; `/c/` does not, because `/c/` has no such field.
 *
 * So the policy is parameterised rather than unified. Everything hard lives
 * here once: the banned list, the suffix matching, truncation, and the rule
 * that nested data is dropped.
 *
 * What `/lp/*` GAINS by adopting this, which is the real point of the change:
 * it previously matched only seven exact key names, so `userEmail`,
 * `recovery_email`, `storyTitle` and `reportHeadline` all sailed through, and
 * it forwarded nested objects verbatim. Those were real holes on the pages
 * that take paid traffic.
 *
 * The design package's acceptance check is "no event contains an email address
 * or free text". That is a privacy commitment, not a nice-to-have, and a leak
 * here is invisible in the UI and survives review, which is why it is asserted
 * rather than trusted to call sites.
 */

/** Keys we will not forward under any circumstances, however they are nested. */
export const BANNED_KEYS = [
  'email', 'brief', 'token', 'sessiontoken', 'session_token', 'name',
  'headline', 'title', 'storytitle', 'story_title', 'text', 'body',
  'recoveryemail', 'recovery_email', 'query', 'answer', 'othertext',
];

/** Long free text is how a brief sneaks through under an innocent key. */
export const MAX_STRING = 64;

/**
 * Suffix matching, not equality: `userEmail`, `recovery_email` and
 * `USER_TOKEN` must all be caught, and an exact-match list never catches the
 * key someone invents next week.
 */
export function isBanned(key, extraBanned = [], allowKeys = []) {
  const k = String(key).toLowerCase().replace(/[^a-z_]/g, '');
  // Suffix matching is blunt by design, and it has false positives: `has_text`
  // ends with `text` but is a boolean, and it is precisely the field the design
  // package says to send INSTEAD of the text. An explicit allowlist is the
  // honest way to say "this one is safe despite the shape", rather than
  // loosening the rule for everything.
  if (allowKeys.includes(k)) return false;
  const list = extraBanned.length ? [...BANNED_KEYS, ...extraBanned] : BANNED_KEYS;
  return list.some((banned) => k === banned || k.endsWith(banned));
}

/**
 * @param {string[]} extraBanned          keys this policy additionally refuses
 * @param {string[]} allowKeys            keys kept despite matching a banned suffix
 * @param {boolean}  allowPrimitiveArrays keep arrays of strings/numbers/booleans
 * @param {boolean}  keepNullish          forward null/undefined rather than dropping
 * @param {number}   maxString            truncate strings beyond this
 * @param {number}   maxArrayLength       cap kept arrays
 */
export function createScrub({
  extraBanned = [],
  allowKeys = [],
  allowPrimitiveArrays = false,
  keepNullish = false,
  maxString = MAX_STRING,
  maxArrayLength = 20,
} = {}) {
  const norm = (k) => String(k).toLowerCase().replace(/[^a-z_]/g, '');
  const banned = extraBanned.map(norm);
  const allowed = allowKeys.map(norm);

  const truncate = (v) => (v.length > maxString ? v.slice(0, maxString) : v);

  return function scrub(params = {}) {
    const out = {};
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) {
        if (keepNullish) out[key] = value;
        continue;
      }
      if (isBanned(key, banned, allowed)) continue;

      if (typeof value === 'number' || typeof value === 'boolean') {
        out[key] = value;
        continue;
      }
      if (typeof value === 'string') {
        // Truncated, not dropped: a long value is more likely a mistake than a
        // deliberate payload, and silently losing the whole event would hide it.
        out[key] = truncate(value);
        continue;
      }
      if (allowPrimitiveArrays && Array.isArray(value)) {
        // Narrow on purpose. `topics: ['Health']` is legitimate;
        // `[{ email }]` is the hole this would otherwise open, so anything
        // that is not a primitive is dropped element by element.
        const kept = value
          .filter((el) => ['string', 'number', 'boolean'].includes(typeof el))
          .slice(0, maxArrayLength)
          .map((el) => (typeof el === 'string' ? truncate(el) : el));
        out[key] = kept;
        continue;
      }
      // Objects, functions and (by default) arrays are dropped outright: no
      // event here needs one, and nested data is where PII hides.
    }
    return out;
  };
}
